import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, FileText, PlusCircle, LogOut, Sparkles, ClipboardList, Settings,
  Copy, Trash2, Edit3, Eye, Loader2, RefreshCw, Calendar, Hash, X, Save, Search
} from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import apiService from '../../services/api';
import { toast } from 'sonner';

const AdminTemplatesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [saving, setSaving] = useState(false);

  // Carregar templates
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await apiService.getTemplates();
      setTemplates(data || []);
    } catch (err) {
      console.error('Erro ao carregar templates:', err);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  // Filtrar templates
  const filteredTemplates = templates.filter(t => 
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.age_range?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Atualizar template
  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await apiService.updateTemplate(editingTemplate.id, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        age_range: editingTemplate.age_range
      });
      toast.success('Template atualizado!');
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      console.error('Erro ao atualizar template:', err);
      toast.error('Erro ao atualizar template');
    } finally {
      setSaving(false);
    }
  };

  // Deletar template
  const handleDeleteTemplate = async (templateId) => {
    try {
      await apiService.deleteTemplate(templateId);
      toast.success('Template excluído!');
      setShowDeleteModal(null);
      loadTemplates();
    } catch (err) {
      console.error('Erro ao deletar template:', err);
      toast.error('Erro ao excluir template');
    }
  };

  // Visualizar plano do template
  const handlePreviewTemplate = (template) => {
    // Abrir em nova aba com preview do conteúdo
    const planData = template.plan_data;
    const previewContent = `
      <html>
        <head>
          <title>Preview: ${template.name}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; background: #FBF4FF; }
            h1 { color: #30244F; }
            h2 { color: #8F7AC5; margin-top: 24px; }
            .summary { background: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; }
            .pillar { background: white; padding: 16px; border-radius: 8px; margin-bottom: 12px; border-left: 4px solid #8F7AC5; }
            .pillar h3 { margin: 0 0 8px 0; color: #30244F; }
            ul { margin: 8px 0; padding-left: 20px; }
          </style>
        </head>
        <body>
          <h1>${template.name}</h1>
          <p><strong>Faixa etária:</strong> ${template.age_range || 'Não definida'}</p>
          <p><strong>Descrição:</strong> ${template.description || 'Sem descrição'}</p>
          
          ${planData.summary ? `<div class="summary"><h2>Resumo</h2><p>${planData.summary}</p></div>` : ''}
          
          ${planData.pillars?.length > 0 ? `
            <h2>Pilares do Plano</h2>
            ${planData.pillars.map(p => `
              <div class="pillar">
                <h3>${p.title || 'Pilar'}</h3>
                ${p.instructions?.length > 0 ? `<ul>${p.instructions.map(i => `<li>${i}</li>`).join('')}</ul>` : ''}
              </div>
            `).join('')}
          ` : ''}
          
          ${planData.finalMessage ? `<h2>Mensagem Final</h2><p>${planData.finalMessage}</p>` : ''}
        </body>
      </html>
    `;
    const blob = new Blob([previewContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-[#FAE0D5] p-6 hidden lg:block">
        <div className="mb-8">
          <Link to="/admin/dashboard">
            <Logo variant="v3SonoRotina" size="md" className="cursor-pointer hover:opacity-80 transition-opacity" />
          </Link>
        </div>

        <nav className="space-y-2">
          {[
            { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
            { icon: PlusCircle, label: 'Novo Relatório', path: '/admin/new' },
            { icon: ClipboardList, label: 'Inserir Manualmente', path: '/admin/manual' },
            { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
            { icon: Copy, label: 'Templates', path: '/admin/templates' },
            { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
            { icon: Settings, label: 'Editor de Formulário', path: '/admin/form-builder' }
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                location.pathname === item.path
                  ? 'bg-[#8F7AC5] text-white'
                  : 'text-[#6B5B8C] hover:bg-[#FBF4FF]'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <Button
            variant="ghost"
            className="w-full justify-start text-[#9890A8] hover:text-[#30244F] hover:bg-[#FBF4FF]"
            onClick={() => navigate('/admin')}
          >
            <LogOut className="w-5 h-5 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        {/* Mobile Header */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center justify-between mb-4">
            <Link to="/admin/dashboard">
              <Logo variant="v3SonoRotina" size="sm" />
            </Link>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/admin')}
              className="text-[#9890A8]"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
          {/* Mobile Navigation Tabs */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {[
              { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
              { icon: FileText, label: 'Relatórios', path: '/admin/reports' },
              { icon: Copy, label: 'Templates', path: '/admin/templates' },
              { icon: Sparkles, label: 'Treinar IA', path: '/admin/training' },
              { icon: Settings, label: 'Formulário', path: '/admin/form-builder' }
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  location.pathname === item.path
                    ? 'bg-[#8F7AC5] text-white'
                    : 'bg-white text-[#6B5B8C] border border-[#FAE0D5]'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-[#30244F]">Templates de Planos</h1>
            <p className="text-[#6B5B8C] mt-1">Reutilize planos aprovados para criar novos planos mais rapidamente</p>
          </div>
          <Button
            onClick={loadTemplates}
            variant="outline"
            className="border-[#8F7AC5] text-[#8F7AC5] hover:bg-[#8F7AC5] hover:text-white"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-[#8F7AC5]/10 to-[#D98DAE]/10 border border-[#8F7AC5]/20 rounded-xl p-4 mb-6"
        >
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#8F7AC5]/20 flex items-center justify-center flex-shrink-0">
              <Copy className="w-4 h-4 text-[#8F7AC5]" />
            </div>
            <div>
              <h3 className="font-medium text-[#30244F]">Como usar templates?</h3>
              <p className="text-sm text-[#6B5B8C] mt-1">
                Após aprovar um plano, você pode salvá-lo como template. 
                Ao gerar um novo plano, escolha "Usar Template" em vez de "Gerar com IA" para aplicar rapidamente.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9890A8]" />
          <Input
            placeholder="Buscar templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-[#FAE0D5] focus:border-[#8F7AC5]"
          />
        </div>

        {/* Templates Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[#FAE0D5] p-12 text-center">
            <Copy className="w-12 h-12 text-[#D98DAE] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#30244F] mb-2">
              {searchTerm ? 'Nenhum template encontrado' : 'Nenhum template criado'}
            </h3>
            <p className="text-[#6B5B8C]">
              {searchTerm 
                ? 'Tente buscar com outros termos' 
                : 'Aprove um plano e clique em "Salvar como Template" para criar seu primeiro template.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl border border-[#FAE0D5] p-5 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#30244F] truncate" title={template.name}>
                      {template.name}
                    </h3>
                    {template.age_range && (
                      <span className="inline-flex items-center gap-1 text-xs bg-[#8F7AC5]/10 text-[#8F7AC5] px-2 py-1 rounded-full mt-1">
                        <Calendar className="w-3 h-3" />
                        {template.age_range}
                      </span>
                    )}
                  </div>
                </div>

                {template.description && (
                  <p className="text-sm text-[#6B5B8C] mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}

                <div className="flex items-center gap-2 text-xs text-[#9890A8] mb-4">
                  <Hash className="w-3 h-3" />
                  <span>Usado {template.usage_count || 0}x</span>
                  <span className="mx-1">•</span>
                  <span>{new Date(template.created_at).toLocaleDateString('pt-BR')}</span>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-[#FAE0D5] text-[#6B5B8C] hover:bg-[#FBF4FF]"
                    onClick={() => handlePreviewTemplate(template)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Ver
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-[#FAE0D5] text-[#6B5B8C] hover:bg-[#FBF4FF]"
                    onClick={() => setEditingTemplate({...template})}
                  >
                    <Edit3 className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-200 text-red-500 hover:bg-red-50"
                    onClick={() => setShowDeleteModal(template.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Edit Modal */}
        <AnimatePresence>
          {editingTemplate && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setEditingTemplate(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-[#30244F]">Editar Template</h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingTemplate(null)}
                    className="text-[#9890A8]"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="text-[#30244F]">Nome do Template *</Label>
                    <Input
                      value={editingTemplate.name}
                      onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      placeholder="Ex: 6-8 meses - associação mamadeira"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Faixa Etária</Label>
                    <Input
                      value={editingTemplate.age_range || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, age_range: e.target.value})}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5]"
                      placeholder="Ex: 6-8 meses"
                    />
                  </div>

                  <div>
                    <Label className="text-[#30244F]">Descrição</Label>
                    <Textarea
                      value={editingTemplate.description || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                      className="mt-1 border-[#FAE0D5] focus:border-[#8F7AC5] min-h-[100px]"
                      placeholder="Descreva quando usar este template..."
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="flex-1 border-[#FAE0D5]"
                    onClick={() => setEditingTemplate(null)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    className="flex-1 bg-[#8F7AC5] hover:bg-[#7A68A8] text-white"
                    onClick={handleUpdateTemplate}
                    disabled={!editingTemplate.name || saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Salvar
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Delete Confirmation Modal */}
        <AnimatePresence>
          {showDeleteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
              onClick={() => setShowDeleteModal(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-2xl p-6 max-w-sm w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <Trash2 className="w-6 h-6 text-red-500" />
                  </div>
                  <h3 className="text-lg font-bold text-[#30244F] mb-2">Excluir template?</h3>
                  <p className="text-[#6B5B8C] mb-6">Esta ação não pode ser desfeita.</p>
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="flex-1 border-[#FAE0D5]"
                      onClick={() => setShowDeleteModal(null)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                      onClick={() => handleDeleteTemplate(showDeleteModal)}
                    >
                      Excluir
                    </Button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AdminTemplatesPage;
